import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { EstimateConfiguration } from '@interfaces/EstimateConfiguration';
import { TranslateService } from '@ngx-translate/core';
import { NGXLogger } from 'ngx-logger';
import { EMPTY, firstValueFrom, lastValueFrom, Observable, ReplaySubject } from 'rxjs';
import { catchError, take, tap } from 'rxjs/operators';
import { BaseService } from './base.service';
import { UserService } from './user.service';

@Injectable({
  providedIn: 'root'
})
export class EstimateSettingsService extends BaseService {
  private estimateConfiguration$ = new ReplaySubject<EstimateConfiguration>(1);

  private isInit = false;

  public constructor(
    private http: HttpClient,
    private logger: NGXLogger,
    private userService: UserService,
    private translateService: TranslateService
  ) {
    super();
  }

  public getEstimateConfigurationAsync(refresh = false) {
    return firstValueFrom(this.getEstimateConfiguration(refresh).pipe(take(1)));
  }

  public getEstimateConfiguration(refresh = true): Observable<EstimateConfiguration> {
    if (refresh || !this.isInit) {
      this.isInit = true;
      this.http
        .get<EstimateConfiguration>(this.baseUrl + '/web/EstimateConfiguration/')
        .pipe(
          tap(x => {
            this.estimateConfiguration$.next(x);
          }),
          catchError((err, caught) => {
            this.logger.error('EstimateSettingsService.getSettings() : ' + err);
            this.logger.error('EstimateSettingsService.getSettings() : ' + caught);
            return EMPTY;
          })
        )
        .subscribe();
    }

    return this.estimateConfiguration$.asObservable();
  }

  public async getDepartmentLabel() {
    const user = await this.userService.getUserAsync();
    const userLang =
      user.languageCode === 'fr' || user.languageCode === 'en' ? user.languageCode : this.translateService.currentLang;
    const config = await firstValueFrom(this.getEstimateConfiguration(false));
    if (userLang.toLowerCase().includes('fr')) {
      return config.departmentLabelFR;
    }
    return config.departmentLabelEN;
  }

  public setEstimateConfiguration(estimateConfiguration: EstimateConfiguration) {
    this.estimateConfiguration$.next(estimateConfiguration);

    return lastValueFrom(
      this.http.post(this.baseUrl + '/web/EstimateConfiguration/', estimateConfiguration).pipe(
        catchError((err, caught) => {
          this.logger.error('EstimateSettingsService.updateSettings() : ' + err);
          this.logger.error('EstimateSettingsService.updateSettings() : ' + caught);
          return EMPTY;
        })
      )
    );
  }

  public getManagerClassificationCodes(config: EstimateConfiguration): string[] {
    return config.managerClassificationCodes?.split(';') ?? [];
  }

  public getActiveClassificationCodes(config: EstimateConfiguration): string[] {
    return config.activeClassificationCodes?.split(';') ?? [];
  }

  public getRestrictedCompanies(config: EstimateConfiguration): string[] {
    return config.restrictedCompanies?.split(';') ?? [];
  }

  public async getRestrictEstimateListAccess() {
    const isAdmin = await this.userService.hasRoleAsync('app-admin');
    const config = await firstValueFrom(this.getEstimateConfiguration(false));

    return !config.restrictEstimateListAccess || isAdmin;
  }
}
